import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import { EmptyValue } from '@/components/EmptyValue'
import { LocalDate } from '@/components/LocalDate'
import { ConfirmDialog } from '@/components/organization/ConfirmDialog'
import { PageHeader } from '@/components/PageHeader'
import { PendingButton } from '@/components/PendingButton'
import { ProviderIcon } from '@/components/ProviderIcon'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import {
  listAllUsers,
  setUserBanned,
  setUserPlatformRole,
} from '@/lib/admin/server'
import { SOCIAL_PROVIDER_LABELS, type SocialProvider } from '@/lib/auth-social'
import { AdminListCard } from './-components/AdminListCard'

export const Route = createFileRoute('/_authenticated/admin/users')({
  // `?q=` prefills the search so other admin pages can link straight to one
  // user (the chat inbox links a ticket's account here by email).
  validateSearch: z.object({ q: z.string().optional() }),
  component: AdminUsersPage,
})

type UserRow = Awaited<ReturnType<typeof listAllUsers>>[number]

/** Grant/revoke platform admin, behind an explicit confirmation. The server
 *  refuses self-demotion, so the worst mis-click here is a toast. */
function RoleAction({ user }: { user: UserRow }) {
  const [confirming, setConfirming] = useState(false)
  const queryClient = useQueryClient()
  const isAdmin = user.role === 'admin'
  const mutation = useMutation({
    mutationFn: () =>
      setUserPlatformRole({ data: { userId: user.id, admin: !isAdmin } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      toast.success(
        isAdmin
          ? `${user.email} is no longer a platform admin`
          : `${user.email} is now a platform admin`,
      )
    },
    onError: (e) =>
      toast.error('Role change failed', {
        description: (e as Error).message,
      }),
  })

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setConfirming(true)}
      >
        {isAdmin ? 'Remove admin' : 'Make admin'}
      </Button>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={isAdmin ? 'Remove platform admin?' : 'Make platform admin?'}
        description={
          isAdmin
            ? `${user.email} will lose access to every admin page and admin server function.`
            : `${user.email} will be able to see and change every organization's billing, credentials, and ledger, and use these support actions.`
        }
        actionLabel={isAdmin ? 'Remove admin' : 'Make admin'}
        variant={isAdmin ? 'default' : 'destructive'}
        onConfirm={() => mutation.mutate()}
      />
    </>
  )
}

/** Ban (with an optional reason) or unban a user across the whole platform.
 *  The server refuses self-ban and revokes the banned user's sessions. */
function BanAction({ user }: { user: UserRow }) {
  const [banOpen, setBanOpen] = useState(false)
  const [unbanOpen, setUnbanOpen] = useState(false)
  const [reason, setReason] = useState('')
  const queryClient = useQueryClient()
  const isBanned = user.banned

  const mutation = useMutation({
    mutationFn: (vars: { banned: boolean; reason?: string }) =>
      setUserBanned({
        data: { userId: user.id, banned: vars.banned, reason: vars.reason },
      }),
    onSuccess: (_r, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      toast.success(
        vars.banned ? `${user.email} is banned` : `${user.email} is unbanned`,
      )
      setBanOpen(false)
    },
    onError: (e) =>
      toast.error('Ban change failed', { description: (e as Error).message }),
  })

  if (isBanned) {
    return (
      <>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setUnbanOpen(true)}
        >
          Unban
        </Button>
        <ConfirmDialog
          open={unbanOpen}
          onOpenChange={setUnbanOpen}
          title={`Unban ${user.email}?`}
          description="They regain the ability to sign in."
          actionLabel="Unban"
          onConfirm={() => mutation.mutate({ banned: false })}
        />
      </>
    )
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setBanOpen(true)}
      >
        Ban
      </Button>
      <Dialog
        open={banOpen}
        onOpenChange={(open) => {
          setBanOpen(open)
          if (!open) setReason('')
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ban {user.email}?</DialogTitle>
            <DialogDescription>
              They are signed out everywhere and cannot sign back in until
              unbanned. Their organizations and data are kept.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="admin-ban-reason">
                Reason (optional)
              </FieldLabel>
              <Textarea
                id="admin-ban-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Kept in support records only."
              />
            </Field>
          </FieldGroup>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBanOpen(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <PendingButton
              variant="destructive"
              pending={mutation.isPending}
              onClick={() =>
                mutation.mutate({
                  banned: true,
                  reason: reason.trim() || undefined,
                })
              }
            >
              Ban user
            </PendingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

const columns: ColumnDef<UserRow>[] = [
  {
    accessorKey: 'email',
    header: 'User',
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex flex-col gap-0.5">
        <div className="font-medium">
          {row.original.name || <EmptyValue>No name</EmptyValue>}
        </div>
        <div className="text-xs text-muted-foreground">
          {row.original.email}
        </div>
      </div>
    ),
  },
  {
    accessorKey: 'role',
    header: 'Role',
    enableSorting: true,
    cell: ({ row }) =>
      row.original.role === 'admin' ? (
        <Badge>admin</Badge>
      ) : (
        <span className="text-muted-foreground">user</span>
      ),
  },
  {
    id: 'orgs',
    accessorFn: (u) => u.ownedOrgs.length,
    header: 'Orgs',
    enableSorting: true,
    cell: ({ row }) => {
      const owned = row.original.ownedOrgs
      if (owned.length === 0) {
        return <span className="tabular-nums">0</span>
      }
      if (owned.length === 1) {
        return (
          <Link
            to="/admin/organizations/$orgId"
            params={{ orgId: owned[0].id }}
            className="font-medium hover:underline"
          >
            {owned[0].name}
          </Link>
        )
      }
      return (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="-ml-2">
              {owned.length} orgs
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="flex w-56 flex-col gap-1 p-2"
            align="start"
          >
            {owned.map((o) => (
              <Link
                key={o.id}
                to="/admin/organizations/$orgId"
                params={{ orgId: o.id }}
                className="rounded-sm px-2 py-1 text-sm hover:bg-accent"
              >
                {o.name}
              </Link>
            ))}
          </PopoverContent>
        </Popover>
      )
    },
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const u = row.original
      return u.banned ? (
        <Badge variant="destructive">banned</Badge>
      ) : u.emailVerified ? (
        <Badge variant="secondary">verified</Badge>
      ) : (
        <span className="text-muted-foreground">unverified</span>
      )
    },
  },
  {
    id: 'signin',
    header: 'Sign-in',
    cell: ({ row }) => {
      const socials = row.original.providers.filter(
        (p): p is SocialProvider => p in SOCIAL_PROVIDER_LABELS,
      )
      if (socials.length === 0) {
        return row.original.providers.includes('credential') ? (
          <span className="text-xs text-muted-foreground">Email</span>
        ) : (
          <EmptyValue>None</EmptyValue>
        )
      }
      return (
        <div className="flex items-center gap-1.5">
          {socials.map((p) => (
            <span
              key={p}
              title={SOCIAL_PROVIDER_LABELS[p]}
              className="inline-flex"
            >
              <ProviderIcon provider={p} className="size-4" />
            </span>
          ))}
        </div>
      )
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Joined',
    enableSorting: true,
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        <LocalDate iso={row.original.createdAt} />
      </span>
    ),
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <div className="flex justify-end gap-1">
        <BanAction user={row.original} />
        <RoleAction user={row.original} />
      </div>
    ),
  },
]

function AdminUsersPage() {
  const { q } = Route.useSearch()
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Users"
        description="Every user account on the platform."
      />
      <AdminListCard
        title="Users"
        noun="user"
        queryKey={['admin-users']}
        queryFn={() => listAllUsers()}
        columns={columns}
        emptyMessage="No users yet."
        searchAccessor={(u) => `${u.name} ${u.email}`}
        searchPlaceholder="Search by name or email"
        initialSearch={q}
      />
    </div>
  )
}
